import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { pageOf } from './page-of';

/** A page opened: its route with ids taken out, its tab, when. */
export interface PageView {
  page: string;
  tab?: string;
  at: number;
}

/** Views wait this long and go together: one request for a burst of navigation. */
export const FLUSH_MS = 15_000;
/** Above this many waiting, they go at once. */
const MAX_QUEUED = 25;

/**
 * Tells the API which pages are opened (the route with its ids taken out, and
 * the tab), in batches. Renders nothing; mounted once in the vendor portal
 * (see VendorUsageTracker). Losing a few views (a closed tab) is fine.
 */
export function PageViewTracker({ record }: { record: (views: PageView[]) => Promise<unknown> }) {
  const location = useLocation();
  const apiRef = useRef({ recordViews: record });
  apiRef.current = { recordViews: record };
  const queue = useRef<PageView[]>([]);
  const last = useRef<string>('');

  const flush = useRef(() => {
    if (!queue.current.length) return;
    const views = queue.current.splice(0, queue.current.length);
    Promise.resolve()
      .then(() => apiRef.current.recordViews(views))
      .catch(() => undefined);
  });

  useEffect(() => {
    const page = pageOf(location.pathname);
    const tab = new URLSearchParams(location.search).get('tab') || undefined;
    const key = `${page}|${tab || ''}`;
    // A re-render, or a search typed into the page, is not another visit.
    if (key === last.current) return;
    last.current = key;
    queue.current.push({ page, ...(tab && /^[\w-]{1,40}$/.test(tab) ? { tab } : {}), at: Date.now() });
    if (queue.current.length >= MAX_QUEUED) flush.current();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const send = () => flush.current();
    const id = window.setInterval(send, FLUSH_MS);
    const onHide = () => document.visibilityState === 'hidden' && send();
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onHide);
      send();
    };
  }, []);

  return null;
}
